import { FunnelStatusName } from 'src/modules/shared/components/atoms/funnel-status-name';
import {
  CustomFieldEntityType,
  CustomFieldType,
  type FunnelConversation,
} from 'src/api/generated';
import { useSearch } from '@tanstack/react-router';
import { Route as CommunicationsRoute } from 'src/routes/communications';
import type { CommunicationsSearch } from 'src/modules/shared/schemas/communications-search.schema';
import { useUpdateFunnelConversation } from 'src/modules/communications/hooks/api/funnels/funnel-conversations-mutations';
import { useAiWorkspaceStatusIdsByStatus } from 'src/modules/shared/hooks/api/automations/employees/workspaces-ai';
import { useCustomFieldDefinitions } from 'src/modules/shared/hooks/api/custom-fields/definitions';
import { useFunnelCustomFieldDefinitions } from 'src/modules/shared/hooks/api/crm/funnels/funnel-custom-field-definitions';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { useCustomFieldListOptions } from 'src/modules/shared/hooks/api/custom-fields/list-options';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { useMemo, useRef, useState } from 'react';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useClientType } from 'src/modules/shared/hooks/use-client-type';
import { useFunnelStatuses } from 'src/modules/shared/hooks/api/crm/funnels/funnel-statuses';
import { buildConversationStatusDropdownOptions } from 'src/modules/communications/utils/status-dropdown-options.util';
import { useTasks } from 'src/modules/shared/hooks/api/tasks/tasks';
import { getStatusVariant } from 'src/modules/shared/utils/status-color.util';
import { useAllPersonAndSystemUsers } from 'src/modules/communications/hooks/users/system-users';
import {
  buildConversationUserOptions,
  getConversationStatusQuery,
  getConversationTaskSummary,
} from 'src/modules/communications/utils/conversation-detail.util';
import { DetailsFieldListSkeleton } from './details-field-list-skeleton-parts';
import { CustomFieldEditor } from '../../custom-fields/custom-field-editor';
import { PropertySelector } from 'src/modules/shared/components/molecules/property-selector';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import Label from 'src/modules/shared/components/atoms/inputs/label';
import { PiListChecks } from 'react-icons/pi';
import { ConversationTasksModal } from 'src/modules/communications/components/organisms/conversation-detail-panel/conversation-tasks-modal';
import { DetailsSectionReveal } from './details-section-reveal';
import {
  stackedFieldTriggerClassName,
  stackedFieldValueClassName,
} from 'src/modules/shared/styles/stacked-field.styles';
import {
  AI_STATUSES_HEADER_ID,
  isStatusDropdownGroupHeader,
} from 'src/modules/communications/utils/status-dropdown-options.util';
import { createCustomFieldFromDefinition } from 'src/modules/shared/utils/custom-fields-factory.util';
import { getCustomFieldKey } from 'src/modules/shared/utils/custom-fields.util';
import { resolveDateCustomFieldIncludeTime } from 'src/modules/shared/utils/custom-field-definition.util';

interface ConversationFieldsSectionProps {
  conversation?: FunnelConversation;
  loading?: boolean;
  readOnly?: boolean;
  error?: Error | null;
}

const getTaskCountLabel = ({
  openTaskCount,
  t,
}: {
  openTaskCount: number;
  t: ReturnType<typeof useI18n>['t'];
}) => {
  if (openTaskCount === 0) {
    return t('communications.contactPanel.conversations.fields.noTasks');
  }

  return t(
    openTaskCount === 1
      ? 'communications.contactPanel.conversations.fields.taskCountSingular'
      : 'communications.contactPanel.conversations.fields.taskCountPlural',
    { count: openTaskCount }
  );
};

const getConversationSubtitle = (conversation: FunnelConversation) =>
  formatFullName(
    conversation.contacts?.[0]?.firstName,
    conversation.contacts?.[0]?.lastName
  ) || undefined;

const renderConversationFieldRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-0.5">
    <Label size="xs" variant="secondary" layout="stacked">
      {label}
    </Label>
    {children}
  </div>
);

export const ConversationFieldsSection: React.FC<
  ConversationFieldsSectionProps
> = ({ conversation, loading = false, readOnly = false, error = null }) => {
  const { t } = useI18n();
  const clientType = useClientType();
  const isRealEstate = clientType === 'REAL_ESTATE';
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [areAiStatusesCollapsed, setAreAiStatusesCollapsed] = useState(true);
  const wasStatusDropdownOpenRef = useRef(false);
  const { updateConversation, loading: isUpdatingConversation } =
    useUpdateFunnelConversation();

  const { funnelId } = useSearch({
    from: CommunicationsRoute.id,
    select: (search: CommunicationsSearch) => ({
      funnelId: search.funnelId,
    }),
  });

  const hasFunnel = !!funnelId;

  const {
    definitions: fallbackDefinitions,
    loading: loadingDefinitions,
    error: definitionsError,
  } = useCustomFieldDefinitions({
    entityType: CustomFieldEntityType.FUNNEL_CONVERSATION,
    enabled: !hasFunnel,
  });

  const {
    definitions: funnelOrderedDefinitions,
    loading: loadingFunnelDefinitions,
    error: funnelDefinitionsError,
  } = useFunnelCustomFieldDefinitions({
    funnelId: hasFunnel ? funnelId : undefined,
    enabled: hasFunnel,
  });

  const definitions = hasFunnel
    ? funnelOrderedDefinitions.filter(
        (definition) =>
          definition.entityType === CustomFieldEntityType.FUNNEL_CONVERSATION
      )
    : fallbackDefinitions;

  const statusQuery = useMemo(
    () => getConversationStatusQuery(conversation?.funnelId ?? undefined),
    [conversation?.funnelId]
  );

  const { statuses, loading: loadingStatuses } = useFunnelStatuses(
    statusQuery,
    !!conversation?.funnelId
  );
  const statusIds = useMemo(
    () => statuses.map((status) => status.id),
    [statuses]
  );
  const { aiStatusIds } = useAiWorkspaceStatusIdsByStatus(
    statusIds,
    statusIds.length > 0
  );
  const { users, loading: loadingUsers } = useAllPersonAndSystemUsers();
  const { tasks } = useTasks({
    conversationId: conversation?.id ?? 0,
    enabled: !!conversation?.id,
  });

  const listDefinitionIds = definitions
    .filter((definition) => definition.fieldType === CustomFieldType.LIST)
    .map((definition) => definition.id);

  const optionsQuery = useMemo(() => {
    if (listDefinitionIds.length === 0) return null;

    return createQuery()
      .equals('definitionId', listDefinitionIds)
      .take(100)
      .build();
  }, [listDefinitionIds]);

  const { options: listOptions } = useCustomFieldListOptions({
    ...(optionsQuery ?? {}),
    enabled: hasFunnel && listDefinitionIds.length > 0,
  });

  const optionsMap = useMemo(() => {
    const map: Record<number, { label: string; value: string }[]> = {};

    listOptions.forEach((option) => {
      const definitionOptions = (map[option.definitionId] ??= []);

      definitionOptions.push({
        label: option.label ?? option.value,
        value: option.value,
      });
    });

    return map;
  }, [listOptions]);

  const statusOptions = useMemo(() => {
    return buildConversationStatusDropdownOptions(
      statuses,
      conversation?.funnelStatusId ?? undefined,
      aiStatusIds,
      areAiStatusesCollapsed
    );
  }, [
    statuses,
    conversation?.funnelStatusId,
    aiStatusIds,
    areAiStatusesCollapsed,
  ]);

  const userOptions = useMemo(() => {
    return buildConversationUserOptions({
      t,
      userId: conversation?.userId,
      users,
    });
  }, [conversation?.userId, t, users]);

  const currentStatus = useMemo(() => {
    return statuses.find(
      (status) => status.id === conversation?.funnelStatusId
    );
  }, [statuses, conversation?.funnelStatusId]);

  const currentStatusVariant = useMemo(
    () => getStatusVariant(currentStatus?.name, currentStatus?.terminalOutcome),
    [currentStatus?.name, currentStatus?.terminalOutcome]
  );

  const currentUser = useMemo(() => {
    return users.find((user) => user.id === conversation?.userId);
  }, [users, conversation?.userId]);

  const { openTaskCount, overdueTaskCount } = useMemo(
    () => getConversationTaskSummary(tasks),
    [tasks]
  );

  const handleUpdateConversationField = async (
    fieldId: string | number,
    value: unknown
  ) => {
    if (readOnly || !conversation) return;

    await updateConversation({
      id: conversation.id,
      data: {
        customFields: {
          ...(conversation.customFields ?? {}),
          [fieldId.toString()]: value,
        },
      },
    });
  };

  const handleStatusChange = async (statusId: number | null) => {
    if (readOnly || !conversation) return;

    await updateConversation({
      id: conversation.id,
      data: { funnelStatusId: statusId },
    });
  };

  const handleUserAssign = async (userId: number | null) => {
    if (readOnly || !conversation) return;

    await updateConversation({
      id: conversation.id,
      data: { userId },
    });
  };

  const isLoadingDefinitions = hasFunnel
    ? loadingFunnelDefinitions
    : loadingDefinitions;
  if (loading || isLoadingDefinitions) {
    return <DetailsFieldListSkeleton fieldCount={4} variant="conversation" />;
  }

  const hasDefinitionsError = hasFunnel
    ? funnelDefinitionsError
    : definitionsError;
  if (error || hasDefinitionsError) {
    return (
      <ErrorState
        title={t(
          'communications.contactPanel.conversations.fields.error.title'
        )}
        description={t(
          'communications.contactPanel.conversations.fields.error.description'
        )}
        variant="primary"
      />
    );
  }

  if (!conversation) {
    return (
      <EmptyState
        description={t(
          'communications.contactPanel.conversations.fields.emptySelect'
        )}
      />
    );
  }

  const currentStatusLabel = (
    <FunnelStatusName
      name={
        currentStatus?.name ??
        t('communications.contactPanel.conversations.fields.noneStatus')
      }
      outcome={currentStatus?.terminalOutcome}
      icon="archive"
      showArchiveTooltip
      className={`truncate ${stackedFieldValueClassName}`}
    />
  );
  const currentAssigneeLabel = (
    <Text size="small" className={`truncate ${stackedFieldValueClassName}`}>
      {currentUser
        ? formatFullName(currentUser.firstName, currentUser.lastName)
        : t('communications.contactPanel.conversations.fields.unassigned')}
    </Text>
  );

  const items: React.ReactNode[] = [
    renderConversationFieldRow({
      label: t('communications.contactPanel.conversations.fields.status'),
      children: (
        <MenuDropdown
          label={currentStatusLabel}
          items={statusOptions.map((option) => ({
            ...option,
            onClick: () => {
              if (isStatusDropdownGroupHeader(option.id)) {
                if (option.id === AI_STATUSES_HEADER_ID) {
                  setAreAiStatusesCollapsed((current) => !current);
                }

                return;
              }

              void handleStatusChange(option.id as number);
            },
          }))}
          className={stackedFieldTriggerClassName}
          handlerContainerClassName="w-full"
          variant={currentStatusVariant}
          appearance="soft"
          size="md"
          disabled={readOnly}
          isLoading={loadingStatuses || isUpdatingConversation}
          mobileTitle={t('communications.dropdown.status.mobileTitle')}
          onOpenChange={(isOpen) => {
            if (isOpen && !wasStatusDropdownOpenRef.current) {
              setAreAiStatusesCollapsed(true);
            }

            wasStatusDropdownOpenRef.current = isOpen;
          }}
        />
      ),
    }),
    renderConversationFieldRow({
      label: t('communications.contactPanel.conversations.fields.assignee'),
      children: (
        <MenuDropdown
          label={currentAssigneeLabel}
          items={userOptions.map((option) => ({
            ...option,
            onClick: () =>
              void handleUserAssign(
                option.id === 'unassign' ? null : (option.id as number)
              ),
          }))}
          className={stackedFieldTriggerClassName}
          handlerContainerClassName="w-full"
          variant="neutral"
          appearance="soft"
          size="md"
          disabled={readOnly}
          isLoading={loadingUsers || isUpdatingConversation}
          mobileTitle={t('communications.dropdown.assignee.mobileTitle')}
        />
      ),
    }),
    renderConversationFieldRow({
      label: t('communications.contactPanel.conversations.fields.tasks'),
      children: (
        <Button
          variant={overdueTaskCount > 0 ? 'danger' : 'neutral'}
          appearance="soft"
          size="md"
          onClick={() => setIsTaskModalOpen(true)}
          className={stackedFieldTriggerClassName}
        >
          <Text
            size="small"
            className={`truncate ${stackedFieldValueClassName}`}
          >
            {getTaskCountLabel({ openTaskCount, t })}
          </Text>
          <PiListChecks className="h-4 w-4 flex-shrink-0" />
        </Button>
      ),
    }),
  ];

  if (isRealEstate) {
    items.push(
      renderConversationFieldRow({
        label: t('shared.property.label'),
        children: (
          <PropertySelector
            className="w-full min-w-0"
            value={conversation.propertyId}
            disabled={readOnly || isUpdatingConversation}
            onChange={(propertyId) => {
              void updateConversation({
                id: conversation.id,
                data: { propertyId },
              });
            }}
            placement="bottom-start"
            size="md"
            showOpenButton
            take={30}
            tooltipContent={t('shared.propertySelector.tooltip')}
            tooltipPlacement="top"
            variant="neutral"
            appearance="soft"
          />
        ),
      })
    );
  }

  definitions.forEach((definition) => {
    const prefixedKey = getCustomFieldKey(
      definition.fieldType,
      definition.fieldKey
    );
    const value =
      (conversation.customFields as Record<string, unknown> | undefined)?.[
        prefixedKey
      ] ?? null;

    const customField = createCustomFieldFromDefinition(
      definition.id,
      definition.fieldKey,
      definition.fieldType,
      definition.fieldLabel,
      value,
      definition.fieldType === CustomFieldType.LIST
        ? optionsMap[definition.id]
        : undefined,
      resolveDateCustomFieldIncludeTime(
        definition.fieldType,
        definition.includeTime
      )
    );

    items.push(
      <CustomFieldEditor
        key={definition.fieldKey}
        field={customField}
        onSave={(value) => handleUpdateConversationField(prefixedKey, value)}
        disabled={isUpdatingConversation || readOnly}
        loading={isUpdatingConversation}
        entityType={CustomFieldEntityType.FUNNEL_CONVERSATION}
        entityId={conversation.id}
        size="md"
        variant="neutral"
        appearance="soft"
        layout="stacked"
      />
    );
  });

  return (
    <div className="flex flex-col gap-3">
      <DetailsSectionReveal items={items} initiallyVisible={4} />

      <ConversationTasksModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
        }}
        tasks={tasks}
        conversationId={conversation.id}
        subtitle={getConversationSubtitle(conversation)}
      />
    </div>
  );
};
